import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Paper,
  alpha,
} from '@mui/material';
import { formatItemDisplayName } from '../utils/formatItemDisplayName';
import { numberToWords } from '../../../shared/utils/numberToWords';
import InvoiceBarcode from '../../../shared/components/InvoiceBarcode';
import { CustomDocumentRenderer } from '../../../shared/components/CustomDocumentRenderer';

const TEMPLATE_KEYS = [
  'custom',
  'standard',
  'thermal',
  'template3',
  'template4',
  'template5',
  'template6',
  'template7',
  'template8',
  'template9',
  'template10',
  'template11',
  'a5',
  'a4_preprinted',
  'a5_preprinted',
];

export function SalesInvoiceTemplateRenderer({
  order,
  company,
  templateKey,
  currency,
  itemFormat,
  dateFormat,
  showTax: propShowTax = true,
}) {
  const isTaxDisabled = company?.taxLabel === 'None' || (Number(company?.vatRate ?? 0) === 0 && Number(order?.vatTotal ?? 0) === 0);
  const showTax = isTaxDisabled ? false : propShowTax;
  const safeTemplateKey = TEMPLATE_KEYS.includes(templateKey) ? templateKey : 'standard';
  const safeCurrency = currency || company?.currency || 'USD';
  const safeItemFormat = itemFormat || company?.printItemNameFormat || 'name_sku';
  const safeDateFormat = dateFormat || company?.dateFormat || 'dd/MM/yyyy';
  const paymentMethodLabel = (() => {
    if (order?.salesType === 'credit') return 'Credit';
    if (order?.payments && Array.isArray(order.payments) && order.payments.length > 0) {
      const methods = Array.from(
        new Set(order.payments.map((p) => (p.method ? p.method.charAt(0).toUpperCase() + p.method.slice(1).toLowerCase() : 'Cash')))
      );
      return methods.join(', ');
    }
    return order?.salesType ? (order.salesType.charAt(0).toUpperCase() + order.salesType.slice(1)) : 'Cash';
  })();
  const isPreprinted = templateKey === 'a4_preprinted' || templateKey === 'a5_preprinted';

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: safeCurrency , minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(amount);
    } catch {
      return `${safeCurrency} ${amount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const formatItemName = (item) => {
    let name = formatItemDisplayName(
      {
        name: item.product?.name || item.name,
        sku: item.product?.sku || item.sku,
        brand: item.product?.brand || item.brand,
        description: item.product?.description || item.description || item.notes,
      },
      safeItemFormat
    );
    if (item.isReturn) {
      name += ` (Return${item.isDamaged ? ' - Damaged' : ''})`;
    }

    let serials = item.serialNumbers;
    if (typeof serials === 'string') {
      try {
        serials = JSON.parse(serials);
      } catch (e) {
        serials = null;
      }
    }
    const validSerials = serials && Array.isArray(serials) ? serials.filter(s => s) : [];
    const showWarranty = company?.showWarrantyOnInvoice !== false;

    return (
      <>
        <span>{name}</span>
        {showWarranty && item.hasWarranty && item.warrantyPeriod && (
          <span style={{ display: 'block', fontSize: '0.85em', color: '#16a34a', marginTop: '2px' }}>
            🛡️ Warranty: {item.warrantyPeriod.name}
          </span>
        )}
        {showWarranty && validSerials.length > 0 && (
          <span style={{ display: 'block', fontSize: '0.85em', color: '#4b5563', marginTop: '1px' }}>
            SN: {validSerials.join(', ')}
          </span>
        )}
      </>
    );
  };

  const formatDate = (dateValue, timeValue) => {
    if (!dateValue) return '';
    const dDate = new Date(dateValue);
    if (Number.isNaN(dDate.getTime())) return '';
    const dTime = timeValue ? new Date(timeValue) : dDate;
    const dd = String(dDate.getDate()).padStart(2, '0');
    const mm = String(dDate.getMonth() + 1).padStart(2, '0');
    const yyyy = dDate.getFullYear();
    let hours = dTime.getHours();
    const minutes = String(dTime.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const timeStr = `${hours}:${minutes} ${ampm}`;
    let dateStr = '';
    switch (safeDateFormat) {
      case 'MM/dd/yyyy':
        dateStr = `${mm}/${dd}/${yyyy}`;
        break;
      case 'yyyy-MM-dd':
        dateStr = `${yyyy}-${mm}-${dd}`;
        break;
      case 'dd-MM-yyyy':
        dateStr = `${dd}-${mm}-${yyyy}`;
        break;
      case 'dd/MM/yyyy':
      default:
        dateStr = `${dd}/${mm}/${yyyy}`;
    }
    return `${dateStr} ${timeStr}`;
  };

  const invoiceDate = formatDate(order?.invoiceDate || order?.orderDate, order?.createdAt);
  const vatIncluded = company?.salesVatIncluded !== false;

  const formatLongDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthName = months[d.getMonth()];
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${monthName} ${day}, ${year}`;
  };

  const formatTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  const formatValue = (value) => {
    const amount = Number(value || 0);
    const decimals = typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2;
    return amount.toFixed(decimals);
  };

  const items = (order?.items || []).map((it) => {
    const qty = Number(it.quantity || 0);
    const vatAmt = Number(it.vatAmount || 0);
    const disc = Number(it.discount || 0);
    const baseRate = Number(it.unitPrice || 0);
    
    const baseAmount = qty * baseRate - disc;
    const suppliedTotal = Number(it.total || baseAmount + vatAmt);

    let rate = baseRate;
    let amount = baseAmount;
    let total = suppliedTotal;

    // If unit prices are VAT-inclusive, extract VAT for invoice line accuracy
    if (vatIncluded && vatAmt > 0) {
      total = baseAmount;
      amount = total - vatAmt;
      rate = qty > 0 ? (amount + disc) / qty : baseRate;
    }

    return {
      ...it,
      unitPrice: rate,
      total: total,
    };
  });

  const totalQuantity = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

  const renderStandard = () => (
    <Box
      sx={{
        p: 4,
        maxWidth: 900,
        m: '0 auto',
        fontSize: 12,
        '@media print': {
          boxShadow: 'none',
          m: 0,
          p: 2,
          pb: isPreprinted ? '25mm' : 2,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, pt: isPreprinted ? '45mm' : 0 }}>
        {!isPreprinted ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {company?.logoUrl && (
              <Box
                component="img"
                src={company.logoUrl}
                alt=""
                sx={{ height: 48, width: 'auto', maxWidth: 120, objectFit: 'contain', display: 'block' }}
              />
            )}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {company?.name || 'Company'}
              </Typography>
              {company?.address && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {company.address}
                </Typography>
              )}
            </Box>
          </Box>
        ) : <Box />}
        <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Invoice
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Invoice No: {order?.invoiceNumber || order?.orderNumber || '-'}
          </Typography>
          <Box sx={{ my: 0.5 }}>
            <InvoiceBarcode value={order?.invoiceNumber || order?.orderNumber} height={28} width={1.2} />
          </Box>
          <Typography variant="body2">Date: {invoiceDate}</Typography>
          <Typography variant="body2">Payment Method: {paymentMethodLabel}</Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ flex: 1, mr: 4 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Bill To
          </Typography>
          <Typography variant="body2">{order?.customer?.name || '-'}</Typography>
          {order?.customer?.address && <Typography variant="body2">{order.customer.address}</Typography>}
          {order?.customer?.trn && <Typography variant="body2">TRN: {order.customer.trn}</Typography>}
        </Box>
        <Box sx={{ flex: 1 }}>
          {order?.location && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Branch / Store
              </Typography>
              <Typography variant="body2">
                {order.location.name} ({order.location.code})
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Table size="small" sx={{ mt: 2 }} aria-label="items">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Qty</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Discount</TableCell>
            {showTax && <TableCell sx={{ fontWeight: 600 }}>VAT</TableCell>}
            <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Line Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => {
            const qty = Number(it.quantity || 0);
            const vatAmt = Number(it.vatAmount || 0);
            const total = Number(it.total || 0);
            const displayRate = showTax ? it.unitPrice : (qty > 0 ? (total + Number(it.discount || 0)) / qty : it.unitPrice);
            const displayAmount = showTax ? (qty * it.unitPrice - Number(it.discount || 0)) : total;

            return (
              <TableRow key={it.id || idx} sx={it.isReturn ? { bgcolor: alpha('#f44336', 0.05) } : {}}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{formatItemName(it)}</Typography>
                  {it.batchNo && <Typography sx={{ fontSize: 11 }}>Batch: {it.batchNo}</Typography>}
                  {it.expiryDate && (
                    <Typography sx={{ fontSize: 11 }}>
                      Exp: {new Date(it.expiryDate).toLocaleDateString()}
                    </Typography>
                  )}
                  {(() => {
                    let details = null;
                    if (it.serviceData) {
                      try {
                        details = typeof it.serviceData === 'string' ? JSON.parse(it.serviceData) : it.serviceData;
                      } catch (e) {
                        console.log("Error parsing serviceData", e);
                      }
                    }
                    if (!details || (!details.deviceModel && !details.imei && !details.complains)) return null;
                    return (
                      <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid purple', color: 'text.secondary', fontSize: 11 }}>
                        {details.deviceModel && <div><strong>Device Model:</strong> {details.deviceModel}</div>}
                        {details.imei && <div><strong>IMEI/Serial:</strong> {details.imei}</div>}
                        {details.complains && <div><strong>Fault/Complains:</strong> {details.complains}</div>}
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell>{qty.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}</TableCell>
                <TableCell>{it.unit || 'pcs'}</TableCell>
                <TableCell>{formatAmount(displayRate)}</TableCell>
                <TableCell>{formatAmount(it.discount || 0)}</TableCell>
                {showTax && <TableCell>{formatAmount(vatAmt)}</TableCell>}
                <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(total)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Box sx={{ width: 260 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">Subtotal</Typography>
            <Typography variant="body2">{formatAmount(order?.subtotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">Discount</Typography>
            <Typography variant="body2">{formatAmount(order?.discountTotal)}</Typography>
          </Box>
          {showTax && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">VAT</Typography>
              <Typography variant="body2">{formatAmount(order?.vatTotal)}</Typography>
            </Box>
          )}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {showTax ? 'Grand Total' : 'Total'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {formatAmount(order?.grandTotal || order?.totalAmount)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">Paid</Typography>
            <Typography variant="body2">{formatAmount(order?.paidAmount)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Balance</Typography>
            <Typography variant="body2">{formatAmount(order?.balanceAmount)}</Typography>
          </Box>
        </Box>
      </Box>

      {order?.notes && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderThermal = () => (
    <Box
      sx={{
        p: 2,
        maxWidth: 420,
        m: '0 auto',
        fontSize: 11,
        '@media print': {
          boxShadow: 'none',
          m: 0,
          p: 1,
          maxWidth: '100%',
        },
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 1 }}>
        {company?.logoUrl && (
          <Box
            component="img"
            src={company.logoUrl}
            alt=""
            sx={{
              height: 36,
              width: 'auto',
              maxWidth: 100,
              objectFit: 'contain',
              mx: 'auto',
              mb: 0.5,
              display: 'block',
            }}
          />
        )}
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {company?.name || 'Company'}
        </Typography>
        {company?.address && (
          <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
            {company.address}
          </Typography>
        )}
        <Typography variant="caption" display="block" sx={{ fontWeight: 700 }}>
          Invoice: {order?.invoiceNumber || order?.orderNumber || '-'}
        </Typography>
        <Box sx={{ my: 0.5, display: 'flex', justifyContent: 'center' }}>
          <InvoiceBarcode value={order?.invoiceNumber || order?.orderNumber} height={28} width={1.1} />
        </Box>
        <Typography variant="caption" display="block">
          Date: {invoiceDate}
        </Typography>
        <Typography variant="caption" display="block">
          Customer: {order?.customer?.name || '-'}
        </Typography>
      </Box>

      <Divider sx={{ my: 1 }} />

      <Table
        size="small"
        aria-label="items"
        sx={{ '& td, & th': { borderBottom: 'none', paddingY: 0.25 } }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 11 }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 11 }} align="right">
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 11 }} align="right">
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell sx={{ fontSize: 11 }}>
                {formatItemName(it)}
                {it.batchNo && (
                  <Typography variant="caption" display="block">
                    Batch: {it.batchNo}
                  </Typography>
                )}
                {(() => {
                  let details = null;
                  if (it.serviceData) {
                    try {
                      details = typeof it.serviceData === 'string' ? JSON.parse(it.serviceData) : it.serviceData;
                    } catch (e) {
                      console.log("Error parsing serviceData", e);
                    }
                  }
                  if (!details || (!details.deviceModel && !details.imei && !details.complains)) return null;
                  return (
                    <Box sx={{ mt: 0.5, pl: 0.5, borderLeft: '1px solid black', color: 'text.secondary', fontSize: 10 }}>
                      {details.deviceModel && <div>Mod: {details.deviceModel}</div>}
                      {details.imei && <div>SN: {details.imei}</div>}
                      {details.complains && <div>Flt: {details.complains}</div>}
                    </Box>
                  );
                })()}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 11 }}>
                {Number(it.quantity || 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 11 }}>
                {formatAmount(it.unitPrice)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 11 }}>
                {formatAmount(it.total || 0)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Subtotal</Typography>
          <Typography variant="caption">{formatAmount(order?.subtotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Discount</Typography>
          <Typography variant="caption">{formatAmount(order?.discountTotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">VAT</Typography>
          <Typography variant="caption">{formatAmount(order?.vatTotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Grand Total
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {formatAmount(order?.grandTotal || order?.totalAmount)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Paid</Typography>
          <Typography variant="caption">{formatAmount(order?.paidAmount)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Balance</Typography>
          <Typography variant="caption">{formatAmount(order?.balanceAmount)}</Typography>
        </Box>
      </Box>

      {order?.notes && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="caption">Thank you for your business!</Typography>
      </Box>
    </Box>
  );

  const renderTemplate3 = () => (
    <Box
      sx={{
        p: '35px 40px',
        maxWidth: 820,
        minHeight: '290mm',
        m: '0 auto',
        fontSize: 10,
        backgroundColor: '#FFFFFF',
        fontFamily: "'Inter', -apple-system, sans-serif",
        color: '#1e293b',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        overflow: 'hidden',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        '@media print': {
          boxShadow: 'none',
          border: 'none',
          m: 0,
          p: '20px 25px',
          maxWidth: '100%',
          height: '290mm',
          '@page': {
            size: 'a4 portrait !important',
            margin: '0mm !important',
          },
        },
      }}
    >
      {/* Top Left Background Geometric SVG */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 320,
          height: 180,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      >
        <svg width="320" height="180" viewBox="0 0 320 180" style={{ position: 'absolute', top: 0, left: 0 }}>
          {/* Black/Grey polygon */}
          <polygon points="0,0 240,0 120,60 0,60" fill="#0f172a" />
          {/* Red polygon */}
          <polygon points="0,0 150,0 0,130" fill="#e11d48" />
          {/* Subtle Accent Grey polygon */}
          <polygon points="120,0 280,0 200,45" fill="#475569" opacity="0.4" />
        </svg>
      </Box>

      {/* Bottom Background Geometric SVG */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: 120,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      >
        <svg width="100%" height="120" viewBox="0 0 820 120" preserveAspectRatio="none" style={{ position: 'absolute', bottom: 0, left: 0 }}>
          {/* Bottom left dark polygon */}
          <polygon points="0,120 0,30 220,120" fill="#0f172a" />
          {/* Bottom left overlapping red polygon */}
          <polygon points="20,120 220,50 360,120" fill="#e11d48" />
          {/* Bottom left grey accent */}
          <polygon points="280,120 380,80 480,120" fill="#475569" opacity="0.8" />
          {/* Bottom right dark polygon */}
          <polygon points="420,120 540,40 820,120" fill="#0f172a" />
          {/* Bottom right red polygon */}
          <polygon points="500,120 620,15 820,120" fill="#e11d48" />
        </svg>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', flexGrow: 1, justifyContent: 'space-between' }}>
        <Box>
          {/* Header section */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {company?.logoUrl ? (
                <Box
                  component="img"
                  src={company.logoUrl}
                  alt=""
                  sx={{ height: 48, width: 'auto', maxWidth: 120, objectFit: 'contain' }}
                />
              ) : (
                <svg width="45" height="45" viewBox="0 0 100 100" fill="none">
                  <polygon points="50,5 95,25 95,75 50,95 5,75 5,25" stroke="#e11d48" strokeWidth="10" />
                  <polygon points="50,20 80,35 80,65 50,80 20,65 20,35" stroke="#0f172a" strokeWidth="5" />
                </svg>
              )}
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: 20, color: '#0f172a', lineHeight: 1.1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {company?.name || 'COMPANY'}
                </Typography>
                <Typography sx={{ fontSize: 8.5, color: '#64748b', letterSpacing: '0.8px', textTransform: 'uppercase', fontWeight: 600, mt: 0.25 }}>
                  {company?.tagline || 'YOUR BUSINESS SLOGAN HERE'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <Typography sx={{ fontWeight: 900, fontSize: 36, color: '#e11d48', textTransform: 'uppercase', letterSpacing: '1px', lineHeight: 1 }}>
                Invoice
              </Typography>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#0f172a', mt: 0.5 }}>
                #{order?.invoiceNumber || order?.orderNumber || '-'}
              </Typography>
              <Box sx={{ my: 0.5 }}>
                <InvoiceBarcode value={order?.invoiceNumber || order?.orderNumber} height={26} width={1.1} />
              </Box>
              <Typography sx={{ fontSize: 9, color: '#64748b', mt: 0.25 }}>Date: {invoiceDate}</Typography>
            </Box>
          </Box>

          {/* Contact Details & Customer Details Grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 4, mb: 4, alignItems: 'start' }}>
            {/* Left Column: Contact details with red round icons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pl: 1 }}>
              {[
                {
                  icon: (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  ),
                  text: company?.phone || '0000 0000 0000',
                },
                {
                  icon: (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  ),
                  text: company?.email || 'yourname@email.com',
                },
                {
                  icon: (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                  ),
                  text: company?.website || 'yourwebsite.com',
                },
                {
                  icon: (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  ),
                  text: company?.address || 'Your Address Here',
                },
              ].map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#e11d48', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {item.icon}
                  </Box>
                  <Typography sx={{ fontSize: 9.5, fontWeight: 500, color: '#475569', whiteSpace: 'pre-line', lineHeight: 1.3 }}>
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Right Column: Customer Details Box */}
            <Box sx={{ border: '1px solid #cbd5e1', borderRadius: '4px', overflow: 'hidden' }}>
              <Box sx={{ backgroundColor: '#f8fafc', py: 0.5, px: 1.5, borderBottom: '1px solid #cbd5e1' }}>
                <Typography sx={{ fontWeight: 800, fontSize: 9.5, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  INVOICE TO:
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {[
                  { label: 'Name:', value: order?.customer?.name || '-' },
                  { label: 'Address:', value: order?.customer?.address || '-' },
                  { label: 'Phone:', value: order?.customer?.phone || '-' },
                  { label: 'TRN:', value: order?.customer?.trn || order?.customer?.taxId || '-' },
                  { label: 'Website:', value: order?.customer?.website || '-' },
                ].filter(item => item.value !== '-').map((item, idx) => (
                  <Box key={idx} sx={{ display: 'grid', gridTemplateColumns: '60px 1fr', gap: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 9, color: '#64748b' }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontSize: 9, color: '#0f172a', whiteSpace: 'pre-line', lineHeight: 1.2 }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Items Table */}
          <Table size="small" sx={{ mb: 2, '& th, & td': { border: '1px solid #cbd5e1' } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#e11d48' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 800, fontSize: 9.5, py: 1, width: '8%', textAlign: 'center', borderColor: '#e11d48' }}>
                  SL NO
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 800, fontSize: 9.5, py: 1, width: '50%', borderColor: '#e11d48' }}>
                  ITEM DESCRIPTION
                </TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 800, fontSize: 9.5, py: 1, width: '14%', borderColor: '#e11d48' }}>
                  UNIT PRICE
                </TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 800, fontSize: 9.5, py: 1, width: '14%', borderColor: '#e11d48' }}>
                  QUANTITY
                </TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 800, fontSize: 9.5, py: 1, width: '14%', borderColor: '#e11d48' }}>
                  TOTAL
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it, idx) => {
                const qty = Number(it.quantity || 0);
                const total = Number(it.total || 0);
                const displayRate = showTax ? it.unitPrice : (qty > 0 ? (total + Number(it.discount || 0)) / qty : it.unitPrice);
                const slNoStr = String(idx + 1).padStart(2, '0');

                return (
                  <TableRow key={it.id || idx}>
                    <TableCell sx={{ textAlign: 'center', fontSize: 9.5, color: '#475569', py: 1 }}>
                      {slNoStr}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: '#0f172a' }}>
                        {formatItemName(it)}
                      </Typography>
                      {it.description && (
                        <Typography sx={{ fontSize: 8.5, color: '#64748b', mt: 0.25, fontStyle: 'italic' }}>
                          {it.description}
                        </Typography>
                      )}
                      {it.batchNo && (
                        <Typography sx={{ fontSize: 8, color: '#64748b', mt: 0.15 }}>
                          Batch: {it.batchNo} {it.expiryDate && `| Exp: ${new Date(it.expiryDate).toLocaleDateString()}`}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 9.5, color: '#0f172a', py: 1 }}>
                      {formatAmount(displayRate)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 9.5, color: '#0f172a', py: 1 }}>
                      {qty.toFixed(typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}
                      <Typography component="span" sx={{ fontSize: 8, color: '#64748b', ml: 0.25 }}>
                        {it.unit || 'pcs'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 9.5, fontWeight: 700, color: '#0f172a', py: 1 }}>
                      {formatAmount(total)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Bottom Area: Info Block & Totals Block */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 4, mt: 3, alignItems: 'start' }}>
            {/* Left Column: Empty space (previously Bank Info, Payment, Terms) */}
            <Box />

            {/* Right Column: Totals and Signature */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
              {/* Totals Table */}
              <Box sx={{ border: '1px solid #cbd5e1', borderRadius: '4px', overflow: 'hidden' }}>
                {[
                  { label: 'SUB TOTAL', value: formatAmount(order?.subtotal) },
                  showTax && { label: `TAX (VAT ${company?.vatRate || '5'}%)`, value: formatAmount(order?.vatTotal) },
                  Number(order?.discountTotal || 0) > 0 && { label: 'DISCOUNT', value: `-${formatAmount(order?.discountTotal)}`, color: '#ef4444' },
                ].filter(Boolean).map((row, idx) => (
                  <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', px: 1.5, py: 0.75, borderBottom: '1px solid #cbd5e1' }}>
                    <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#475569' }}>{row.label}</Typography>
                    <Typography sx={{ fontSize: 9, fontWeight: 700, color: row.color || '#0f172a' }}>{row.value}</Typography>
                  </Box>
                ))}
                {/* Grand Total */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1.5, py: 1, backgroundColor: '#e11d48' }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#fff' }}>Grand Total:</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 900, color: '#fff' }}>
                    {formatAmount(order?.grandTotal || order?.totalAmount)}
                  </Typography>
                </Box>
                {/* Paid/Balance Info */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1.5, py: 0.5, borderTop: '1px solid #cbd5e1' }}>
                  <Typography sx={{ fontSize: 8.5, color: '#64748b' }}>Paid Amount:</Typography>
                  <Typography sx={{ fontSize: 8.5, fontWeight: 600, color: '#0f172a' }}>{formatAmount(order?.paidAmount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1.5, py: 0.5 }}>
                  <Typography sx={{ fontSize: 8.5, color: '#64748b' }}>Balance Due:</Typography>
                  <Typography sx={{ fontSize: 8.5, fontWeight: 700, color: Number(order?.balanceAmount || 0) > 0 ? '#b91c1c' : '#0f172a' }}>
                    {formatAmount(order?.balanceAmount)}
                  </Typography>
                </Box>
              </Box>

              {/* Signature Block */}
              <Box sx={{ textAlign: 'center', mt: 1, pr: 2, alignSelf: 'flex-end', minWidth: 160 }}>
                {/* Dynamic handwritten name signature style */}
                <Typography sx={{ fontFamily: "'Brush Script MT', 'Comic Sans MS', cursive", fontSize: 16, color: '#0f172a', mb: 0.25 }}>
                  {company?.authorisedSignatoryName || 'Thomas Tucker'}
                </Typography>
                <Box sx={{ borderBottom: '1px solid #0f172a', width: '100%', mb: 0.5 }} />
                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: '#0f172a' }}>
                  {company?.authorisedSignatoryName || 'Thomas Tucker'}
                </Typography>
                <Typography sx={{ fontSize: 8, color: '#64748b' }}>
                  {company?.authorisedSignatoryTitle || 'Managing Director'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const renderTemplate4 = () => (
    <Box
      sx={{
        p: 3,
        maxWidth: 1000,
        m: '0 auto',
        fontSize: 11,
        '@media print': {
          boxShadow: 'none',
          m: 0,
          p: 2,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
          {company?.logoUrl && (
            <Box component="img" src={company.logoUrl} alt="" sx={{ height: 44, width: 'auto', maxWidth: 110, objectFit: 'contain' }} />
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {company?.name || 'Company'}
            </Typography>
            {company?.address && (
              <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
                {company.address}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ flex: 1, textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            TAX INVOICE
          </Typography>
          <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
            Invoice: {order?.invoiceNumber || order?.orderNumber || '-'}
          </Typography>
          <Box sx={{ my: 0.5 }}>
            <InvoiceBarcode value={order?.invoiceNumber || order?.orderNumber} height={26} width={1.1} />
          </Box>
          <Typography variant="caption" display="block">
            Date: {invoiceDate}
          </Typography>
          <Typography variant="caption" display="block">
            Payment Method: {paymentMethodLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Template 4 – Summary
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Customer
          </Typography>
          <Typography variant="caption" display="block">
            {order?.customer?.name || '-'}
          </Typography>
          {order?.customer?.address && (
            <Typography variant="caption" display="block">
              {order.customer.address}
            </Typography>
          )}
          {order?.customer?.trn && (
            <Typography variant="caption" display="block">
              TRN: {order.customer.trn}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          {order?.location && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Branch / Store
              </Typography>
              <Typography variant="caption" display="block">
                {order.location.name} ({order.location.code})
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Table size="small" sx={{ mt: 1 }} aria-label="items">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Disc.
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              VAT
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Line Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>
                <Typography sx={{ fontSize: 11, fontWeight: 500 }}>{formatItemName(it)}</Typography>
              </TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.discount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.vatAmount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Box sx={{ flex: 1 }}>
          {order?.notes && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Notes
              </Typography>
              <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-line' }}>
                {order.notes}
              </Typography>
            </>
          )}
        </Box>
        <Box sx={{ width: 260 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Subtotal</Typography>
            <Typography variant="caption">{formatAmount(order?.subtotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Discount</Typography>
            <Typography variant="caption">{formatAmount(order?.discountTotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">VAT</Typography>
            <Typography variant="caption">{formatAmount(order?.vatTotal)}</Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Grand Total
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {formatAmount(order?.grandTotal || order?.totalAmount)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Paid</Typography>
            <Typography variant="caption">{formatAmount(order?.paidAmount)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Balance</Typography>
            <Typography variant="caption">{formatAmount(order?.balanceAmount)}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const renderTemplate5 = () => (
    <Box
      sx={{
        p: 3,
        maxWidth: 900,
        m: '16px auto',
        fontSize: 12,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#FFFFFF',
        '@media print': {
          m: 0,
          borderRadius: 0,
          borderColor: '#CCCCCC',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {company?.logoUrl && (
            <Box component="img" src={company.logoUrl} alt="" sx={{ height: 48, width: 'auto', maxWidth: 120, objectFit: 'contain' }} />
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {company?.name || 'Company'}
            </Typography>
            {company?.address && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {company.address}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Invoice
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>No: {order?.invoiceNumber || order?.orderNumber || '-'}</Typography>
          <Box sx={{ my: 0.5 }}>
            <InvoiceBarcode value={order?.invoiceNumber || order?.orderNumber} height={26} width={1.1} />
          </Box>
          <Typography variant="body2">Date: {invoiceDate}</Typography>
          <Typography variant="body2">Payment Method: {paymentMethodLabel}</Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Bill To
          </Typography>
          <Typography variant="body2">{order?.customer?.name || '-'}</Typography>
          {order?.customer?.address && <Typography variant="body2">{order.customer.address}</Typography>}
          {order?.customer?.trn && <Typography variant="body2">TRN: {order.customer.trn}</Typography>}
        </Box>
        <Box sx={{ flex: 1, textAlign: 'right' }}>
          {order?.location && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Branch / Store
              </Typography>
              <Typography variant="body2">
                {order.location.name} ({order.location.code})
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Table
        size="small"
        aria-label="items"
        sx={{
          mt: 1,
          '& th, & td': {
            borderBottom: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.100' }}>
            <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Unit
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Discount
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              VAT
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Line Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>
                <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{formatItemName(it)}</Typography>
              </TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}</TableCell>
              <TableCell align="right">{it.unit || 'pcs'}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.discount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.vatAmount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Box
          sx={{
            width: 260,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            p: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Subtotal</Typography>
            <Typography variant="body2">{formatAmount(order?.subtotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Discount</Typography>
            <Typography variant="body2">{formatAmount(order?.discountTotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">VAT</Typography>
            <Typography variant="body2">{formatAmount(order?.vatTotal)}</Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Grand Total
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {formatAmount(order?.grandTotal || order?.totalAmount)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Paid</Typography>
            <Typography variant="body2">{formatAmount(order?.paidAmount)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Balance</Typography>
            <Typography variant="body2">{formatAmount(order?.balanceAmount)}</Typography>
          </Box>
        </Box>
      </Box>

      {order?.notes && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderTemplate6 = () => (
    <Box
      sx={{
        p: 3,
        maxWidth: 920,
        m: '0 auto',
        fontSize: 12,
        border: '2px solid',
        borderColor: 'grey.300',
        borderRadius: 1,
        '@media print': { boxShadow: 'none', m: 0, borderColor: '#999' },
      }}
    >
      <Box sx={{ borderBottom: '2px solid', borderColor: 'grey.300', pb: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {company?.logoUrl && (
              <Box component="img" src={company.logoUrl} alt="" sx={{ height: 48, width: 'auto', maxWidth: 120, objectFit: 'contain' }} />
            )}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                {company?.name || 'Company'}
              </Typography>
              {company?.address && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>
                  {company.address}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              DETAILED INVOICE
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>No: {order?.invoiceNumber || order?.orderNumber || '-'}</Typography>
            <Box sx={{ my: 0.5 }}>
              <InvoiceBarcode value={order?.invoiceNumber || order?.orderNumber} height={26} width={1.1} />
            </Box>
            <Typography variant="body2">Date: {invoiceDate}</Typography>
            <Typography variant="body2">Payment Method: {paymentMethodLabel}</Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          mb: 2,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Bill To
          </Typography>
          <Typography variant="body2">{order?.customer?.name || '-'}</Typography>
          {order?.customer?.address && <Typography variant="body2">{order.customer.address}</Typography>}
          {order?.customer?.trn && <Typography variant="body2">TRN: {order.customer.trn}</Typography>}
        </Box>
        {order?.location && (
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Branch / Store
            </Typography>
            <Typography variant="body2">
              {order.location.name} ({order.location.code})
            </Typography>
          </Box>
        )}
      </Box>
      <Table
        size="small"
        sx={{ '& th, & td': { border: '1px solid', borderColor: 'divider' } }}
        aria-label="items"
      >
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.200' }}>
            <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Qty</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Discount
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              VAT
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Line Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>
                <Typography sx={{ fontWeight: 500 }}>{formatItemName(it)}</Typography>
                {it.batchNo && <Typography variant="caption">Batch: {it.batchNo}</Typography>}
                {it.expiryDate && (
                  <Typography variant="caption" display="block">
                    Exp: {new Date(it.expiryDate).toLocaleDateString()}
                  </Typography>
                )}
              </TableCell>
              <TableCell>{Number(it.quantity || 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}</TableCell>
              <TableCell>{it.unit || 'pcs'}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.discount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.vatAmount || 0)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {formatAmount(it.total || 0)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Box sx={{ width: 280, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">Subtotal</Typography>
            <Typography variant="body2">{formatAmount(order?.subtotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">Discount</Typography>
            <Typography variant="body2">{formatAmount(order?.discountTotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">VAT</Typography>
            <Typography variant="body2">{formatAmount(order?.vatTotal)}</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Grand Total
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {formatAmount(order?.grandTotal || order?.totalAmount)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="body2">Paid</Typography>
            <Typography variant="body2">{formatAmount(order?.paidAmount)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Balance</Typography>
            <Typography variant="body2">{formatAmount(order?.balanceAmount)}</Typography>
          </Box>
        </Box>
      </Box>
      {order?.notes && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderTemplate7 = () => (
    <Box sx={{ p: 2, maxWidth: 800, m: '0 auto', fontSize: 11, '@media print': { m: 0, p: 1 } }}>
      <Box
        sx={{
          textAlign: 'center',
          borderBottom: '3px solid',
          borderColor: 'primary.main',
          pb: 1.5,
          mb: 1.5,
        }}
      >
        {company?.logoUrl && (
          <Box component="img" src={company.logoUrl} alt="" sx={{ height: 36, width: 'auto', maxWidth: 100, objectFit: 'contain', mx: 'auto', mb: 0.5, display: 'block' }} />
        )}
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          {company?.name || 'Company'}
        </Typography>
        {company?.address && (
          <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
            {company.address}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Customer:
          </Typography>
          <Typography variant="body2">{order?.customer?.name || '-'}</Typography>
        </Box>
        <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>Invoice #{order?.invoiceNumber || order?.orderNumber || '-'}</Typography>
          <Box sx={{ my: 0.5 }}>
            <InvoiceBarcode value={order?.invoiceNumber || order?.orderNumber} height={26} width={1.1} />
          </Box>
          <Typography variant="body2">Date: {invoiceDate}</Typography>
        </Box>
      </Box>
      <Table size="small" aria-label="items" sx={{ '& td, & th': { py: 0.4 } }}>
        <TableHead>
          <TableRow sx={{ borderBottom: '2px solid', borderColor: 'grey.400' }}>
            <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>
                <Typography sx={{ fontSize: 11 }}>{formatItemName(it)}</Typography>
              </TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.100', borderRadius: 1, maxWidth: 320, ml: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">Subtotal</Typography>
          <Typography variant="body2">{formatAmount(order?.subtotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">Discount</Typography>
          <Typography variant="body2">{formatAmount(order?.discountTotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">VAT</Typography>
          <Typography variant="body2">{formatAmount(order?.vatTotal)}</Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ fontWeight: 800, fontSize: 13 }}>
            TOTAL
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 800, fontSize: 13 }}>
            {formatAmount(order?.grandTotal || order?.totalAmount)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption">Paid</Typography>
          <Typography variant="caption">{formatAmount(order?.paidAmount)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Balance</Typography>
          <Typography variant="caption">{formatAmount(order?.balanceAmount)}</Typography>
        </Box>
      </Box>
      {order?.notes && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Notes:
          </Typography>
          <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderTemplate8 = () => (
    <Box sx={{ p: 3, maxWidth: 700, m: '0 auto', fontSize: 12, '@media print': { m: 0, p: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {company?.logoUrl && (
            <Box component="img" src={company.logoUrl} alt="" sx={{ height: 44, width: 'auto', maxWidth: 110, objectFit: 'contain' }} />
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {company?.name || 'Company'}
            </Typography>
            {company?.address && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {company.address}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Typography variant="overline" sx={{ fontWeight: 700 }}>
            SERVICE INVOICE
          </Typography>
          <Typography variant="body2" display="block" sx={{ fontWeight: 600 }}>
            No: {order?.invoiceNumber || order?.orderNumber || '-'}
          </Typography>
          <Box sx={{ my: 0.5 }}>
            <InvoiceBarcode value={order?.invoiceNumber || order?.orderNumber} height={26} width={1.1} />
          </Box>
          <Typography variant="body2" display="block">
            Date: {invoiceDate}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Bill To
        </Typography>
        <Typography variant="body2">{order?.customer?.name || '-'}</Typography>
        {order?.customer?.address && <Typography variant="body2">{order.customer.address}</Typography>}
      </Box>
      <Table size="small" aria-label="items">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Rate
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Amount
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>
                <Typography sx={{ fontWeight: 500 }}>{formatItemName(it)}</Typography>
                {(() => {
                  let details = null;
                  if (it.serviceData) {
                    try {
                      details = typeof it.serviceData === 'string' ? JSON.parse(it.serviceData) : it.serviceData;
                    } catch (e) {
                      console.log("Error parsing serviceData", e);
                    }
                  }
                  if (!details || (!details.deviceModel && !details.imei && !details.complains)) return null;
                  return (
                    <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid purple', color: 'text.secondary', fontSize: 11 }}>
                      {details.deviceModel && <div><strong>Device Model:</strong> {details.deviceModel}</div>}
                      {details.imei && <div><strong>IMEI/Serial:</strong> {details.imei}</div>}
                      {details.complains && <div><strong>Fault/Complains:</strong> {details.complains}</div>}
                    </Box>
                  );
                })()}
              </TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">Subtotal</Typography>
          <Typography variant="body2">{formatAmount(order?.subtotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">Discount</Typography>
          <Typography variant="body2">{formatAmount(order?.discountTotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">VAT</Typography>
          <Typography variant="body2">{formatAmount(order?.vatTotal)}</Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Amount Due
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {formatAmount(order?.grandTotal || order?.totalAmount)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="caption">Paid</Typography>
          <Typography variant="caption">{formatAmount(order?.paidAmount)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Balance</Typography>
          <Typography variant="caption">{formatAmount(order?.balanceAmount)}</Typography>
        </Box>
      </Box>
      {order?.notes && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderTemplate9 = () => (
    <Box sx={{ p: 2, maxWidth: 1100, m: '0 auto', fontSize: 11, '@media print': { m: 0 } }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 2, alignItems: 'start' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {company?.logoUrl && (
            <Box component="img" src={company.logoUrl} alt="" sx={{ height: 44, width: 'auto', maxWidth: 110, objectFit: 'contain', flexShrink: 0 }} />
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {company?.name || 'Company'}
            </Typography>
            {company?.address && (
              <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
                {company.address}
              </Typography>
            )}
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                Bill To
              </Typography>
              <Typography variant="body2" display="block">
                {order?.customer?.name || '-'}
              </Typography>
              {order?.customer?.address && (
                <Typography variant="caption" display="block">
                  {order.customer.address}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            INVOICE
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>No: {order?.invoiceNumber || order?.orderNumber || '-'}</Typography>
          <Box sx={{ my: 0.5 }}>
            <InvoiceBarcode value={order?.invoiceNumber || order?.orderNumber} height={26} width={1.1} />
          </Box>
          <Typography variant="body2">Date: {invoiceDate}</Typography>
          <Typography variant="body2">Payment Method: {paymentMethodLabel}</Typography>
          {order?.location && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              {order.location.name} ({order.location.code})
            </Typography>
          )}
        </Box>
      </Box>
      <Divider sx={{ my: 1 }} />
      <Table size="small" aria-label="items" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell sx={{ fontWeight: 600, width: '5%' }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '35%' }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '12%' }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600, width: '14%' }} align="right">
              Unit Price
            </TableCell>
            <TableCell sx={{ fontWeight: 600, width: '14%' }} align="right">
              Disc.
            </TableCell>
            <TableCell sx={{ fontWeight: 600, width: '14%' }} align="right">
              VAT
            </TableCell>
            <TableCell sx={{ fontWeight: 600, width: '16%' }} align="right">
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>
                <Typography sx={{ fontSize: 11 }}>{formatItemName(it)}</Typography>
              </TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.discount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.vatAmount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Box sx={{ width: 280 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Subtotal</Typography>
            <Typography variant="caption">{formatAmount(order?.subtotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Discount</Typography>
            <Typography variant="caption">{formatAmount(order?.discountTotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">VAT</Typography>
            <Typography variant="caption">{formatAmount(order?.vatTotal)}</Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Grand Total
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {formatAmount(order?.grandTotal || order?.totalAmount)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption">Paid</Typography>
            <Typography variant="caption">{formatAmount(order?.paidAmount)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Balance</Typography>
            <Typography variant="caption">{formatAmount(order?.balanceAmount)}</Typography>
          </Box>
        </Box>
      </Box>
      {order?.notes && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderTemplate10 = () => (
    <Box sx={{ p: 3, maxWidth: 880, m: '0 auto', fontSize: 12, '@media print': { m: 0 } }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          border: '1px solid',
          borderColor: 'primary.light',
          borderRadius: 2,
          bgcolor: alpha('#1976d2', 0.04),
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {company?.logoUrl && (
              <Box component="img" src={company.logoUrl} alt="" sx={{ height: 44, width: 'auto', maxWidth: 110, objectFit: 'contain' }} />
            )}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                {company?.name || 'Company'}
              </Typography>
              {company?.address && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {company.address}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.dark' }}>
              Invoice
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>No: {order?.invoiceNumber || order?.orderNumber || '-'}</Typography>
            <Box sx={{ my: 0.5 }}>
              <InvoiceBarcode value={order?.invoiceNumber || order?.orderNumber} height={26} width={1.1} />
            </Box>
            <Typography variant="body2">Date: {invoiceDate}</Typography>
          </Box>
        </Box>
      </Paper>
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Customer
        </Typography>
        <Typography variant="body2">{order?.customer?.name || '-'}</Typography>
        {order?.customer?.address && <Typography variant="body2">{order.customer.address}</Typography>}
      </Paper>
      <Table
        size="small"
        component={Paper}
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
        aria-label="items"
      >
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>{formatItemName(it)}</TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          p: 2,
          border: '1px solid',
          borderColor: 'primary.light',
          borderRadius: 2,
          maxWidth: 300,
          ml: 'auto',
          bgcolor: alpha('#1976d2', 0.04),
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">Subtotal</Typography>
          <Typography variant="body2">{formatAmount(order?.subtotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">Discount</Typography>
          <Typography variant="body2">{formatAmount(order?.discountTotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">VAT</Typography>
          <Typography variant="body2">{formatAmount(order?.vatTotal)}</Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Grand Total
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {formatAmount(order?.grandTotal || order?.totalAmount)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption">Paid</Typography>
          <Typography variant="caption">{formatAmount(order?.paidAmount)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Balance</Typography>
          <Typography variant="caption">{formatAmount(order?.balanceAmount)}</Typography>
        </Box>
      </Paper>
      {order?.notes && (
        <Paper elevation={0} sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Paper>
      )}
    </Box>
  );

  // ── Template 11 – Image-based style (Tax invoice grid + signatures) ─────
  const renderTemplate11 = () => {
    const taxLabel = company?.taxLabel || 'VAT';
    const vatRateLabel = (() => {
      const r = company?.vatRate;
      if (r == null || r === '') return `${taxLabel}`;
      const n = Number(r);
      return Number.isFinite(n) ? `${taxLabel} ${n}%` : `${taxLabel}`;
    })();

    const totalAfterVat = Number(order?.grandTotal || order?.totalAmount || 0);
    const vatTotal = Number(order?.vatTotal || 0);
    const totalBeforeVat = totalAfterVat - vatTotal;

    const toWords = (amount) => {
      // Simple English words (good enough for preview & printing; can be upgraded later)
      const ones = [
        '',
        'One',
        'Two',
        'Three',
        'Four',
        'Five',
        'Six',
        'Seven',
        'Eight',
        'Nine',
        'Ten',
        'Eleven',
        'Twelve',
        'Thirteen',
        'Fourteen',
        'Fifteen',
        'Sixteen',
        'Seventeen',
        'Eighteen',
        'Nineteen',
      ];
      const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
      const numAmount = Math.abs(Number(amount || 0));
      const n = Math.floor(numAmount);
      const fraction = Math.round((numAmount - n) * 100);
      const toWordsInt = (x) => {
        if (x === 0) return '';
        if (x < 20) return ones[x];
        if (x < 100) return tens[Math.floor(x / 10)] + (x % 10 ? ` ${ones[x % 10]}` : '');
        if (x < 1000)
          return (
            ones[Math.floor(x / 100)] +
            ' Hundred' +
            (x % 100 ? ` ${toWordsInt(x % 100)}` : '')
          );
        if (x < 1000000)
          return (
            `${toWordsInt(Math.floor(x / 1000))} Thousand` + (x % 1000 ? ` ${toWordsInt(x % 1000)}` : '')
          );
        return String(amount);
      };
      const whole = toWordsInt(n).trim() || 'Zero';
      if (fraction === 0) return whole;
      const coinName =
        safeCurrency === 'AED' || safeCurrency === 'BHD' || safeCurrency === 'KWD' || safeCurrency === 'OMR'
          ? 'Fils'
          : safeCurrency === 'SAR'
          ? 'Halalas'
          : safeCurrency === 'QAR'
          ? 'Dirhams'
          : 'Cents';
      return `${whole} and ${toWordsInt(fraction).trim()} ${coinName}`;
    };

    const ITEMS_FIRST_PAGE = 15;
    const ITEMS_NEXT_PAGE = 20;
    
    const pages = [];
    if (items.length <= ITEMS_FIRST_PAGE) {
      pages.push(items);
    } else {
      pages.push(items.slice(0, ITEMS_FIRST_PAGE));
      let rest = items.slice(ITEMS_FIRST_PAGE);
      while (rest.length > 0) {
        pages.push(rest.slice(0, ITEMS_NEXT_PAGE));
        rest = rest.slice(ITEMS_NEXT_PAGE);
      }
    }

    return (
      <Box className="print-multi-page-wrapper">
        {pages.map((pageItems, pageIdx) => {
          const isFirstPage = pageIdx === 0;
          const isLastPage = pageIdx === pages.length - 1;
          const startIndex = isFirstPage ? 0 : ITEMS_FIRST_PAGE + (pageIdx - 1) * ITEMS_NEXT_PAGE;

          return (
            <Box
              key={pageIdx}
              sx={{
                  p: 3,
                  maxWidth: 900,
                  m: '0 auto',
                  fontSize: 11,
                  border: '1px solid',
                  borderColor: 'grey.350',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '277.4mm', // Exact A4 height to ensure border consistency
                  boxSizing: 'border-box',
                  pageBreakAfter: !isLastPage ? 'always' : 'auto',
                  background: '#fff',
                  mb: !isLastPage ? 4 : 0,
                  '@media print': { 
                    m: 0, 
                    p: 2.5, 
                    borderColor: 'grey.400', 
                    height: '277.4mm', 
                    display: 'flex', 
                    mb: 0,
                    boxShadow: 'none',
                    border: '1px solid #ccc'
                  },
                }}
              >
          {/* Watermark */}
          {company?.logoUrl && (
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                zIndex: 0,
                opacity: 0.05,
                '@media print': {
                  position: 'fixed',
                  WebkitPrintColorAdjust: 'exact',
                  printColorAdjust: 'exact',
                },
              }}
            >
              <Box
                component="img"
                src={company.logoUrl}
                alt=""
                sx={{
                  width: '80%',
                  maxWidth: 700,
                  filter: 'grayscale(100%)',
                  transform: 'rotate(-15deg)',
                }}
              />
            </Box>
          )}

          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header Content */}
            {isFirstPage && (
              <>
                <Box
                  sx={{
                    pb: 1.5,
                    borderBottom: '2px solid',
                    borderColor: 'grey.800',
                    display: 'grid',
                    gridTemplateColumns: company?.logoUrl ? '140px 1fr 140px' : '1fr',
                    alignItems: 'center',
                    columnGap: 2,
                  }}
                >
                  {company?.logoUrl && (
                    <Box
                      component="img"
                      src={company.logoUrl}
                      alt=""
                      sx={{ width: 130, height: 130, objectFit: 'contain', justifySelf: 'start' }}
                    />
                  )}

                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', fontSize: '2.4rem', color: '#111' }}>
                      {company?.name || 'Company'}
                    </Typography>
                    {company?.nameArabic && (
                      <Typography variant="h5" sx={{ fontWeight: 700, mt: -0.5, mb: 1, color: '#333', fontSize: '1.8rem' }}>
                        {company.nameArabic}
                      </Typography>
                    )}
                    <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 800, textTransform: 'uppercase', color: 'primary.dark' }}>
                      {showTax ? 'TAX INVOICE' : 'INVOICE'}
                    </Typography>
                    {(company?.vatNumber || company?.taxId) && (
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        TRN: {company?.vatNumber || company?.taxId}
                      </Typography>
                    )}
                  </Box>

                  {company?.logoUrl && <Box sx={{ width: 130, height: 130 }} />}
                </Box>

                {/* Info block */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    borderBottom: '1px solid',
                    borderColor: 'grey.400',
                  }}
                >
                  <Box sx={{ p: 1.5, borderRight: '1px solid', borderColor: 'grey.400' }}>
                    {[
                      ['Name', order?.customer?.name],
                      ['Address', order?.customer?.address],
                      ['Tel No', order?.customer?.phone],
                      ['Sales Rep', order?.salesRep],
                      ['Cust TRN', order?.customer?.trn],
                    ]
                      .filter(([, v]) => v)
                      .map(([k, v]) => (
                        <Box key={k} sx={{ display: 'grid', gridTemplateColumns: '95px 1fr', columnGap: 1, mb: 0.25 }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, fontSize: 11 }}>
                            {k}:
                          </Typography>
                          <Typography variant="caption" sx={{ whiteSpace: 'pre-line', fontSize: 11 }}>
                            {String(v)}
                          </Typography>
                        </Box>
                      ))}
                  </Box>
                  <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Box>
                      {[
                        ['Invoice No', order?.invoiceNumber || order?.orderNumber],
                        ['Invoice Date', invoiceDate],
                        ['LPO No', order?.lpoNo],
                        ['LPO Date', order?.lpoDate ? formatDate(order.lpoDate) : undefined],
                        ['DO No', order?.doNo],
                        ['Job No', order?.jobNo],
                      ]
                        .filter(([, v]) => v)
                        .map(([k, v]) => (
                          <Box key={k} sx={{ display: 'grid', gridTemplateColumns: '95px 1fr', columnGap: 1, mb: 0.25 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, fontSize: 11 }}>
                              {k}:
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: 11, fontWeight: k === 'Invoice No' ? 700 : 400 }}>{String(v)}</Typography>
                          </Box>
                        ))}
                    </Box>
                    <Box sx={{ mt: 0.5, textAlign: 'center' }}>
                      <InvoiceBarcode value={order?.invoiceNumber || order?.orderNumber} height={24} width={1.0} />
                    </Box>
                  </Box>
                </Box>
              </>
            )}

            {/* Table wrapper */}
            <Box sx={{ mt: 0, flex: 1 }}>
              <Table
                size="small"
                sx={{
                  '& th, & td': {
                    borderBottom: '1px solid',
                    borderColor: 'grey.300',
                    fontSize: 10.5,
                    py: 0.75,
                  },
                  '& thead': { display: 'table-header-group' },
                  '& thead th': {
                    borderBottom: '2px solid #000',
                    fontWeight: 900,
                    bgcolor: alpha('#000', 0.02),
                    fontSize: 11,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 45 }}>SNo</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell sx={{ width: 65 }} align="right">Qty</TableCell>
                    <TableCell sx={{ width: 85 }} align="right">Rate</TableCell>
                    {showTax && <TableCell sx={{ width: 85 }} align="right">Amount</TableCell>}
                    {showTax && (
                      <>
                        <TableCell sx={{ width: 65 }} align="right">Vat Rate</TableCell>
                        <TableCell sx={{ width: 75 }} align="right">Vat Amt</TableCell>
                      </>
                    )}
                    <TableCell sx={{ width: 100 }} align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageItems.map((it, idx) => {
                    const actualIdx = startIndex + idx;
                    const qty = Number(it.quantity || 0);
                    const total = Number(it.total || 0);
                    const displayRate = showTax ? it.unitPrice : (qty > 0 ? (total + Number(it.discount || 0)) / qty : it.unitPrice);
                    const displayAmount = showTax ? (qty * it.unitPrice - Number(it.discount || 0)) : total;

                    return (
                      <TableRow key={idx}>
                        <TableCell>{actualIdx + 1}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 10.5, fontWeight: 700 }}>{formatItemName(it)}</Typography>
                          {it.description && (
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.2 }}>
                              {it.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{qty.toFixed((company?.currencyDecimals ?? 2))}</TableCell>
                        <TableCell align="right">{formatAmount(displayRate)}</TableCell>
                        {showTax && <TableCell align="right">{formatAmount(displayAmount)}</TableCell>}
                        {showTax && (
                          <>
                            <TableCell align="right">{company?.vatRate}%</TableCell>
                            <TableCell align="right">{formatAmount(it.vatAmount)}</TableCell>
                          </>
                        )}
                        <TableCell align="right">{formatAmount(total)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {/* Last Page Summary Section */}
            {isLastPage && (
              <Box sx={{ mt: 'auto', pageBreakInside: 'avoid', display: 'block' }}>
                {/* Summary Table */}
                <Box
                  sx={{
                    display: 'flex',
                    borderTop: '2px solid #000',
                    borderBottom: '1px solid',
                    borderColor: 'grey.350',
                  }}
                >
                  <Box sx={{ flex: 1, p: 1, borderRight: '1px solid', borderColor: 'grey.350' }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', mb: 0.25, fontSize: 10 }}>
                      Amount in words ({safeCurrency}):
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#000', lineHeight: 1.1 }}>
                      {toWords(totalAfterVat)} Only
                    </Typography>
                  </Box>
                  <Box sx={{ width: 260, flexShrink: 0 }}>
                    {[
                      ['Total Quantity', Number(totalQuantity).toFixed((company?.currencyDecimals ?? 2))],
                      items.some(it => it.isReturn) && ['Total Sales Amount', formatAmount(items.filter(it => !it.isReturn).reduce((sum, it) => sum + Number(it.total || 0), 0))],
                      items.some(it => it.isReturn) && ['Total Return Amount', formatAmount(items.filter(it => it.isReturn).reduce((sum, it) => sum + Number(it.total || 0), 0))],
                      showTax && [`Total Before ${taxLabel}`, formatAmount(totalBeforeVat)],
                      showTax && [vatRateLabel, formatAmount(order?.vatTotal)],
                    ].filter(Boolean).map(([label, val], i, arr) => (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', px: 1, py: 0.5, borderBottom: i < arr.length - 1 ? '1px solid' : 'none', borderColor: 'grey.300' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10 }}>{label}</Typography>
                        <Typography variant="caption" sx={{ fontSize: 10 }}>{val}</Typography>
                      </Box>
                    ))}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, py: 0.75, bgcolor: alpha('#000', 0.02), borderTop: '1px solid #000' }}>
                      <Typography variant="caption" sx={{ fontWeight: 900, fontSize: '0.95rem' }}>{items.some(it => it.isReturn) ? 'Balance Amount' : (showTax ? `Total After ${taxLabel}` : 'Total')}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 900, fontSize: '0.95rem' }}>{formatAmount(totalAfterVat)}</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Signature/Company section */}
                <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'grey.350' }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, fontSize: 10.5 }}>
                    Cheque should be issued in favour of {company?.name || 'Company'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid', borderColor: 'grey.350' }}>
                  <Box sx={{ p: 1, borderRight: '1px solid', borderColor: 'grey.350' }}>
                    {['Receiver’s Name', 'Receiver’s Mobile', 'Receiver’s Signature'].map((label) => (
                      <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="caption" sx={{ width: 120, fontWeight: 800, fontSize: 10 }}>{label}:</Typography>
                        <Box sx={{ flex: 1, borderBottom: '1px dotted #888', height: 14 }} />
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, fontSize: 10 }}>For</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 900, fontSize: 11 }}>{company?.name || 'Company'}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', mt: 3 }}>
                      <Box sx={{ width: 180, ml: 'auto', borderBottom: '1px solid #000', height: 24 }} />
                      <Typography variant="caption" sx={{ fontWeight: 800, mt: 0.5, display: 'block', fontSize: 10 }}>Authorised Signature</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Unified Company Footer Inside Border */}
                <Box sx={{ px: 1.5, py: 1, textAlign: 'center', bgcolor: alpha('#000', 0.01) }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.85rem', display: 'block' }}>
                    {(company?.phone || company?.email) && (
                      <>
                        {company?.phone ? `Tel: ${company.phone}` : ''}
                        {company?.phone && company?.email ? '  |  ' : ''}
                        {company?.email ? `Email: ${company.email}` : ''}
                        {company?.website ? `  |  Web: ${company.website}` : ''}
                      </>
                    )}
                  </Typography>
                  {company?.address && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mt: 0.15, fontSize: '0.8rem' }}>
                      {company.address}
                    </Typography>
                  )}
                  {(company?.vatNumber || company?.taxId) && (
                    <Typography variant="caption" sx={{ fontWeight: 800, mt: 0.25, display: 'block', fontSize: '0.85rem' }}>
                      TRN: {company?.vatNumber || company?.taxId}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
            );
          })}
        </Box>
      );
    };

    const renderA5 = () => {
      const longInvoiceDate = formatLongDate(order?.invoiceDate || order?.orderDate || order?.createdAt);
      const invoiceTime = formatTime(order?.createdAt || order?.orderDate);
      const servedBy = order?.salesPerson
        ? `${order.salesPerson.firstName || ''} ${order.salesPerson.lastName || ''}`.trim()
        : 'admin';

      const minRows = 11;
      const paddingRowsCount = Math.max(0, minRows - items.length);
      const paddedRows = [...items];
      for (let i = 0; i < paddingRowsCount; i++) {
        paddedRows.push({ isPadding: true });
      }

      const taxLabel = company?.taxLabel || 'VAT';
      const vatRateLabel = company?.vatRate != null ? `${taxLabel} (${company.vatRate}%)` : taxLabel;

      return (
        <Box
          sx={{
            p: '15px',
            pb: isPreprinted ? '25mm' : '15px',
            maxWidth: '148mm',
            minHeight: '210mm',
            display: 'flex',
            flexDirection: 'column',
            m: '0 auto',
            fontSize: '9px',
            backgroundColor: '#FFFFFF',
            fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
            color: '#0f172a',
            position: 'relative',
            boxSizing: 'border-box',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)',
            '@media print': {
              boxShadow: 'none',
              border: 'none',
              borderRadius: 0,
              m: 0,
              p: '2mm',
              pb: isPreprinted ? '25mm' : '2mm',
              maxWidth: '100%',
              minHeight: '198mm',
              '@page': {
                size: 'a5 portrait !important',
                margin: '3mm !important',
              },
            },
          }}
        >
          {/* PAID Watermark */}
          {Number(order?.balanceAmount || 0) <= 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%) rotate(-12deg)',
                border: '4px double #1E3A8A',
                color: '#1E3A8A',
                padding: '6px 16px',
                borderRadius: '4px',
                fontFamily: '"Courier New", Courier, monospace',
                fontWeight: 'bold',
                fontSize: '24px',
                textTransform: 'uppercase',
                letterSpacing: '2px',
                opacity: 0.12,
                pointerEvents: 'none',
                zIndex: 100,
                textAlign: 'center',
              }}
            >
              PAID
              <Typography sx={{ fontSize: 7, fontWeight: 'bold', letterSpacing: 0, mt: 0.2 }}>
                {invoiceDate}
              </Typography>
            </Box>
          )}

          {/* Logo Watermark */}
          {company?.logoUrl && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                opacity: 0.08,
                pointerEvents: 'none',
                zIndex: 0,
                width: '60%',
                maxWidth: '300px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Box
                component="img"
                src={company.logoUrl}
                alt="watermark"
                sx={{ width: '100%', height: 'auto', objectFit: 'contain', filter: 'grayscale(100%)' }}
              />
            </Box>
          )}

          {/* Centered Header */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', mb: 1, pt: isPreprinted ? '45mm' : 0 }}>
            {!isPreprinted && (
              <>
                {company?.logoUrl && (
                  <Box
                    component="img"
                    src={company.logoUrl}
                    alt=""
                    sx={{ height: 38, width: 'auto', maxWidth: 100, objectFit: 'contain', mb: 0.5 }}
                  />
                )}
                <Typography sx={{ fontWeight: 800, fontSize: 14, color: '#0f172a', lineHeight: 1.2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {company?.name || 'Company Name'}
                </Typography>
                {company?.tagline && (
                  <Typography sx={{ fontWeight: 700, fontSize: 8.5, color: '#64748b', mt: 0.25, textTransform: 'uppercase', letterSpacing: '1px' }}>
                    {company.tagline}
                  </Typography>
                )}
                {company?.address && (
                  <Typography sx={{ fontSize: 8.5, color: '#475569', whiteSpace: 'pre-line', lineHeight: 1.2, mt: 0.25 }}>
                    {company.address}
                  </Typography>
                )}
                {(company?.phone || company?.email || company?.website) && (
                  <Typography sx={{ fontSize: 8.5, color: '#475569', mt: 0.25 }}>
                    {[
                      company?.phone ? `Mob: ${company.phone}` : '',
                      company?.email ? `Email: ${company.email}` : '',
                      company?.website ? `Web: ${company.website}` : ''
                    ].filter(Boolean).join('  |  ')}
                  </Typography>
                )}
                {company?.taxId && (
                  <Box sx={{ borderTop: '1px dashed #cbd5e1', borderBottom: '1px dashed #cbd5e1', px: 1, py: 0.2, mt: 0.5 }}>
                    <Typography sx={{ fontSize: 8.5, fontWeight: 700, color: '#0f172a', letterSpacing: '0.5px' }}>
                      TRN: {company.taxId}
                    </Typography>
                  </Box>
                )}
              </>
            )}
            
            <Box
              sx={{
                mt: 1,
                mb: 0.25,
                borderTop: '1px solid #0f172a',
                borderBottom: '1px solid #0f172a',
                py: '2px',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontWeight: 900, fontSize: 11, color: '#0f172a', letterSpacing: '1px' }}>
                TAX INVOICE
              </Typography>
            </Box>
          </Box>

          {/* Metadata Block */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '10px',
              mb: 1,
              p: '6px 10px',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
              <Box sx={{ display: 'flex', gap: '4px', fontSize: '9px', alignItems: 'center' }}>
                <span style={{ fontWeight: 'bold', color: '#475569', minWidth: '65px' }}>Invoice No:</span>
                <span style={{ fontWeight: '700', color: '#0f172a' }}>{order?.invoiceNumber || order?.orderNumber || '-'}</span>
              </Box>
              <Box sx={{ my: 0.25 }}>
                <InvoiceBarcode value={order?.invoiceNumber || order?.orderNumber} height={20} width={0.9} />
              </Box>
              <Box sx={{ display: 'flex', gap: '4px', fontSize: '9px' }}>
                <span style={{ fontWeight: 'bold', color: '#475569', minWidth: '65px' }}>Customer:</span>
                <span style={{ color: '#0f172a' }}>{order?.customer?.name || order?.customerName || 'Walk In'}</span>
              </Box>
              {order?.customer?.trn && (
                <Box sx={{ display: 'flex', gap: '4px', fontSize: '9px' }}>
                  <span style={{ fontWeight: 'bold', color: '#475569', minWidth: '65px' }}>Cust TRN:</span>
                  <span style={{ color: '#0f172a' }}>{order.customer.trn}</span>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: '4px', fontSize: '9px' }}>
                <span style={{ fontWeight: 'bold', color: '#475569', minWidth: '65px' }}>Terminal:</span>
                <span style={{ color: '#0f172a' }}>{order?.terminal || 'Terminal 1'}</span>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
              <Box sx={{ display: 'flex', gap: '4px', fontSize: '9px' }}>
                <span style={{ fontWeight: 'bold', color: '#475569', minWidth: '50px' }}>Date:</span>
                <span style={{ color: '#0f172a' }}>{longInvoiceDate}</span>
              </Box>
              <Box sx={{ display: 'flex', gap: '4px', fontSize: '9px' }}>
                <span style={{ fontWeight: 'bold', color: '#475569', minWidth: '50px' }}>Time:</span>
                <span style={{ color: '#0f172a' }}>{invoiceTime}</span>
              </Box>
              <Box sx={{ display: 'flex', gap: '4px', fontSize: '9px' }}>
                <span style={{ fontWeight: 'bold', color: '#475569', minWidth: '50px' }}>Served By:</span>
                <span style={{ color: '#0f172a' }}>{servedBy}</span>
              </Box>
            </Box>
          </Box>

          {/* Items Table */}
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '1.5px solid #0f172a',
            fontSize: '9px',
            fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
            color: '#0f172a'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#0f172a', color: '#ffffff' }}>
                <th style={{ width: '7%', padding: '4px 4px', textAlign: 'center', fontWeight: 'bold' }}>S/L</th>
                <th style={{ width: '53%', padding: '4px 6px', textAlign: 'left', fontWeight: 'bold' }}>Description</th>
                <th style={{ width: '10%', padding: '4px 4px', textAlign: 'center', fontWeight: 'bold' }}>Qty</th>
                <th style={{ width: '15%', padding: '4px 4px', textAlign: 'right', fontWeight: 'bold' }}>Price</th>
                <th style={{ width: '15%', padding: '4px 4px', textAlign: 'right', fontWeight: 'bold' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {paddedRows.map((it, idx) => {
                if (it.isPadding) {
                  return (
                    <tr key={`pad-${idx}`}>
                      <td style={{ padding: '4px 4px', height: '24px' }}>&nbsp;</td>
                      <td style={{ padding: '4px 4px', height: '24px' }}>&nbsp;</td>
                      <td style={{ padding: '4px 4px', height: '24px' }}>&nbsp;</td>
                      <td style={{ padding: '4px 4px', height: '24px' }}>&nbsp;</td>
                      <td style={{ padding: '4px 4px', height: '24px' }}>&nbsp;</td>
                    </tr>
                  );
                }

                const qty = Number(it.quantity || 0);
                const displayPrice = it.unitPrice;
                const displayAmount = it.total;

                return (
                  <tr key={it.id || idx}>
                    <td style={{ padding: '4px 4px', textAlign: 'center', verticalAlign: 'top' }}>
                      {idx + 1}
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'left', verticalAlign: 'top' }}>
                      <div style={{ fontWeight: 'bold' }}>{formatItemName(it)}</div>
                      {/* Render IMEI if present */}
                      {(() => {
                        let details = null;
                        if (it.serviceData) {
                          try {
                            details = typeof it.serviceData === 'string' ? JSON.parse(it.serviceData) : it.serviceData;
                          } catch (e) {
                            console.log("Error parsing serviceData", e);
                          }
                        }
                        if (details && details.imei) {
                          return (
                            <div style={{ fontSize: '8px', color: '#475569', fontWeight: 'bold', marginTop: '1px' }}>
                              IMEI - {details.imei}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </td>
                    <td style={{ padding: '4px 4px', textAlign: 'center', verticalAlign: 'top' }}>
                      {qty.toFixed(0)}
                    </td>
                    <td style={{ padding: '4px 4px', textAlign: 'right', verticalAlign: 'top' }}>
                      {formatValue(displayPrice)}
                    </td>
                    <td style={{ padding: '4px 4px', textAlign: 'right', verticalAlign: 'top' }}>
                      {formatValue(displayAmount)}
                    </td>
                  </tr>
                );
              })}

              {/* Total (Subtotal) Row */}
              <tr style={{ borderTop: '1.5px solid #0f172a' }}>
                <td colSpan="4" style={{ padding: '4px 6px', fontWeight: 'bold', textAlign: 'right' }}>Total</td>
                <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 'bold' }}>
                  {formatValue(order?.subtotal)}
                </td>
              </tr>

              {/* Discount Row (If applicable) */}
              {Number(order?.discountTotal || 0) > 0 && (
                <tr>
                  <td colSpan="4" style={{ padding: '4px 6px', fontWeight: 'bold', textAlign: 'right', color: '#e11d48' }}>Discount</td>
                  <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 'bold', color: '#e11d48' }}>
                    -{formatValue(order?.discountTotal)}
                  </td>
                </tr>
              )}

              {/* VAT Row */}
              <tr>
                <td colSpan="4" style={{ padding: '4px 6px', fontWeight: 'bold', textAlign: 'right' }}>{vatRateLabel}</td>
                <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 'bold' }}>
                  {formatValue(order?.vatTotal)}
                </td>
              </tr>

              {/* Net Total Row */}
              <tr style={{ borderTop: '1.5px solid #0f172a', backgroundColor: '#f1f5f9' }}>
                <td colSpan="4" style={{ padding: '5px 6px', fontWeight: '800', fontSize: '10px', textAlign: 'right' }}>Net Total ({safeCurrency})</td>
                <td style={{ padding: '5px 6px', textAlign: 'right', fontWeight: '900', fontSize: '10.5px' }}>
                  {formatValue(order?.grandTotal || order?.totalAmount)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Amount in Words */}
          <Box sx={{ mt: 0.5, p: '4px 8px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
            <Typography sx={{ fontSize: '9px', fontWeight: 'bold', color: '#0f172a' }}>
              Amount in words:
            </Typography>
            <Typography sx={{ fontSize: '8.5px', color: '#475569', fontStyle: 'italic', mt: 0.2 }}>
              {numberToWords(order?.grandTotal || order?.totalAmount)} {safeCurrency} Only.
            </Typography>
          </Box>

          {/* Spacer to push footer to the bottom */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Footer Section */}
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 1 }}>
            <Box sx={{ maxWidth: '60%' }}>
              {order?.notes && (
                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 'bold', color: '#475569', fontSize: '8.5px', mb: '2px' }}>Notes:</Typography>
                  <Typography sx={{ color: '#0f172a', whiteSpace: 'pre-line', fontSize: '8px', lineHeight: 1.2 }}>{order.notes}</Typography>
                </Box>
              )}
              <Typography sx={{ fontSize: 8.5, fontWeight: 'bold', color: '#475569', fontStyle: 'italic' }}>
                Thank you for your business!
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '130px' }}>
              <Box sx={{ width: '100%', borderBottom: '1px dashed #475569', mb: 0.5 }} />
              <Typography sx={{ fontSize: 8, fontWeight: '700', color: '#475569' }}>Authorized Signature</Typography>
            </Box>
          </Box>

          {/* Unified Company Footer Inside Border */}
          {!isPreprinted && (
            <Box sx={{ borderTop: '1px solid #cbd5e1', pt: 0.5, mt: 0.5, textAlign: 'center', backgroundColor: '#f8fafc', borderRadius: '0 0 8px 8px' }}>
              <Typography sx={{ fontWeight: 800, fontSize: '8px', color: '#0f172a' }}>
                {(company?.phone || company?.email) && (
                  <>
                    {company?.phone ? `Tel: ${company.phone}` : ''}
                    {company?.phone && company?.email ? '  |  ' : ''}
                    {company?.email ? `Email: ${company.email}` : ''}
                    {company?.website ? `  |  Web: ${company.website}` : ''}
                  </>
                )}
              </Typography>
              {company?.address && (
                <Typography sx={{ color: '#475569', fontWeight: 600, display: 'block', mt: 0.2, fontSize: '7.5px' }}>
                  {company.address}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      );
    };

    if (safeTemplateKey === 'custom') {
      const isQuot = order?.docType === 'quotation' || !!order?.quotationNumber;
      return (
        <CustomDocumentRenderer
          docType={isQuot ? 'quotation' : 'invoice'}
          order={order}
          quotation={isQuot ? (order?.quotation || order) : undefined}
          company={company}
          designSettings={company?.invoiceDesignSettings}
          currency={safeCurrency}
          itemFormat={safeItemFormat}
          dateFormat={safeDateFormat}
        />
      );
    }
    if (safeTemplateKey === 'thermal') return renderThermal();
    if (safeTemplateKey === 'a5' || safeTemplateKey === 'a5_preprinted') return renderA5();
    if (safeTemplateKey === 'template3') return renderTemplate3();
    if (safeTemplateKey === 'template4') return renderTemplate4();
    if (safeTemplateKey === 'template5') return renderTemplate5();
    if (safeTemplateKey === 'template6') return renderTemplate6();
    if (safeTemplateKey === 'template7') return renderTemplate7();
    if (safeTemplateKey === 'template8') return renderTemplate8();
    if (safeTemplateKey === 'template9') return renderTemplate9();
    if (safeTemplateKey === 'template10') return renderTemplate10();
    if (safeTemplateKey === 'template11' || safeTemplateKey === 'template12') return renderTemplate11();
    if (safeTemplateKey === 'a4_preprinted') return renderStandard();
    return renderStandard();
  }

